import { useEffect, useState } from "react";
import { Grid, InputAdornment } from "@mui/material";
import {
  CustomTextFeild,
  CustomInputLabel,
  CommonButton,
  CustomCalendarTodayOutlinedIcon,
  CustomRequiredInputLabel,
  CommonOutlinedButton,
  HcccCard,
} from "../common/StyledComponents/StyledFields";
import { apiRequest } from "../../infrastructure/backendService";
import styles from "./hallContractForm.module.scss";
import apiPath from "../../config/apiPaths.json";
import { TextFieldForPrice } from "./textFieldForPrice";
import { TextFieldForCount } from "./textFieldForCount";
import toast from "react-hot-toast";
import constants from "../../config/constants.json";
import { TimeTextField } from "./textFieldForTime";

interface FinalSettlementComponentProps {
  hallContractId?: number;
  contractId?: number;
  handleSubmitReconciliation?: () => void;
}

const keys = [
  "priest",
  "round",
  "rectangle",
  "internet",
  "music",
  "mahaprasadam",
] as const;
type Key = (typeof keys)[number];

const FinalSettlementComponent = (props: FinalSettlementComponentProps) => {
  const [values, setValues] = useState({
    priest: { count: 0, price: 0, total: 0 },
    round: { count: 0, price: 0, total: 0 },
    rectangle: { count: 0, price: 0, total: 0 },
    internet: { count: 0, price: 0, total: 0 },
    music: { count: 0, price: 0, total: 0 },
    mahaprasadam: { count: 0, price: 0, total: 0 },
  });

  const [hallActualData, setHallActualData] = useState<any>([]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handlePriceChange = (id: Key, newValue: number) => {
    setValues((prevValues) => ({
      ...prevValues,
      [id]: {
        ...prevValues[id],
        price: newValue,
        total: newValue * prevValues[id].count,
      },
    }));
  };

  const handleCountChange = (id: Key, newValue: number) => {
    setValues((prevValues) => ({
      ...prevValues,
      [id]: {
        ...prevValues[id],
        count: newValue,
        total: newValue * prevValues[id].price,
      },
    }));
  };

  const handleHallActualHours = (id: number, newValue: number) => {
    let hallsData = hallActualData?.map((data: any, index: number) => {
      if (index === id) {
        return { ...data, eventDuration: newValue };
      }
      return data;
    });
    setHallActualData([...hallsData]);
  };

  const handleTimeChange = (id: number, newTime: string) => {
    let hallsData = hallActualData?.map((data: any, index: number) => {
      if (index === id) {
        return {
          ...data,
          eventStartTime: newTime,
        };
      }
      return data;
    });
    setHallActualData([...hallsData]);
  };

  const handleHallPriceChange = (id: number, newValue: number) => {
    let hallsData = hallActualData?.map((data: any, index: number) => {
      if (index === id) {
        return {
          ...data,
          totalAmount: newValue,
        };
      }
      return data;
    });
    setHallActualData([...hallsData]);
  };

  const handleHoursDurationChange = (id: number) => {
    let hallsData = hallActualData?.map((data: any, index: number) => {
      if (index === id) {
        let hall = specificEventHall(data.eventHallId);
        let totalActual =
          hall?.minimumFee +
          hall?.setupAndCleaning +
          hall?.securityDeposit +
          (data.eventDuration - hall?.minimumHours) * hall?.hourlyFee;
        return {
          ...data,
          eventEndTime: calculateEndTime(
            data.eventStartTime,
            Number(data.eventDuration)
          ),
          totalAmount: totalActual,
        };
      }
      return data;
    });
    setHallActualData([...hallsData]);
  };

  const getTotal = () => {
    let total =
      values.priest.total +
      values.round.total +
      values.rectangle.total +
      values.music.total +
      values.internet.total +
      values.mahaprasadam.total;
    return total;
  };
  let totalDeposit = 0;
  let totalHallsActual = 0;

  const getEstimatedTotal = () => {
    return getTotal() + totalHallsActual - totalDeposit;
  };

  const [formData, setFormData] = useState({
    date: "",
    devoteeName: "",
    address: "",
  });
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [eventHallsResult, setEventHallsResult] = useState<any[]>([]);
  const [result, setResult] = useState<any>(null);
  const [hallId, setHallId] = useState(0);
  useEffect(() => {
    handleData();
  }, []);

  const specificEventHall = (id?: number) => {
    for (const each of eventHallsResult) {
      for (const hall of each.eventHalls) {
        if (hall.id == id) {
          return hall;
        }
      }
    }
    return null;
  };

  const handleData = async () => {
    try {
      const res = await apiRequest({
        method: "GET",
        endpoint: `${apiPath.contractFormHallBookingManagerFlow}/${props.hallContractId}/contracts/${props.contractId}`,
        includeToken: true,
      });
      setResult(res.data);
      setHallActualData([...res?.data?.eventHallChargesBreakdown]);
    } catch (err) {
      console.log(err);
    }
  };

  const getFacilityUsageActualCharges = (hall: any) => {
    return {
      id: hall?.eventHallId,
      name: hall?.eventHallName,
      hourlyFee: hall?.hourlyFee,
      furnitureSetupBreakdownCleaningFee:
        hall?.furnitureSetupBreakdownCleaningFee,
      securityDeposit: hall?.securityDeposit,
      eventDate: hall?.eventDate,
      eventDuration: hall?.eventDuration,
      eventStartTime: hall?.eventStartTime,
      eventEndime: hall?.eventEndTime,
      totalAmount: hall?.totalAmount,
    };
  };

  const reconcilationPayload = {
    facilityUsageActualCharges:
      hallActualData?.length > 0
        ? [
            ...hallActualData?.map((hall: any) =>
              getFacilityUsageActualCharges(hall)
            ),
          ]
        : [],
    eventDuration: result?.eventDuration,
    eventGuestCount: result?.eventGuestCount,
    eventRequiredRoomsCount: result?.eventHallChargesBreakdown?.length,
    eventHallOtherCharges: [
      {
        feeCategory: "Priest Charges",
        duration: Number(values.priest.count),
        hourlyFee: Number(values.priest.price),
        totalFee: values.priest.total,
      },
      {
        feeCategory: "Round Tables",
        duration: Number(values.round.count),
        hourlyFee: Number(values.round.price),
        totalFee: values.round.total,
      },
      {
        feeCategory: "Rectangular Tables",
        duration: Number(values.rectangle.count),
        hourlyFee: Number(values.rectangle.price),
        totalFee: values.rectangle.total,
      },
      {
        feeCategory: "Music System",
        duration: Number(values.music.count),
        hourlyFee: Number(values.music.price),
        totalFee: values.music.total,
      },
      {
        feeCategory: "Internet",
        duration: Number(values.internet.count),
        hourlyFee: Number(values.internet.price),
        totalFee: values.internet.total,
      },
      {
        feeCategory: "Mahaprasadam",
        duration: Number(values.mahaprasadam.count),
        hourlyFee: Number(values.mahaprasadam.price),
        totalFee: values.mahaprasadam.total,
      },
    ],
  };

  const handleSubmit = async () => {
    try {
      const res = await apiRequest({
        method: "PATCH",
        endpoint: `${apiPath.contractFormHallBookingManagerFlow}/${props.hallContractId}/contracts/${props.contractId}/reconciliations`,
        includeToken: true,
        body: JSON.stringify(reconcilationPayload),
      });
      if (res.status === 200) {
        props.handleSubmitReconciliation?.();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert("Something went wrong...");
      }
    } catch (err) {
      console.log(err, "reconsilationError");
      // toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
      //   duration: constants.TOAST_POPUP_DURATION,
      // });
      alert("Something went wrong...");
    }
  };

  const handleDatePickerClick = () => {
    setDatePickerOpen(true);
  };

  useEffect(() => {
    handleEventHallsData();
  }, []);

  const handleEventHallsData = async () => {
    try {
      const res = await apiRequest({
        method: "GET",
        endpoint: `${apiPath.hallTableInfo}`,
        includeToken: true,
      });
      setEventHallsResult(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setDatePickerOpen(false);
  }, [formData.date]);

  const header = () => {
    return (
      <div>
        <h2>Final Settlement of Charges</h2>
        <h4>Usage of Events Halls & Event Rooms</h4>
      </div>
    );
  };
  const depositTableComponent = () => {
    return (
      <div>
        <h4>Deposit/Advance</h4>
        <div
          style={{ borderTopLeftRadius: "5px", borderTopRightRadius: "5px" }}
        >
          <table className={styles.table}>
            <thead>
              <tr className={styles.headerTableRow}>
                <th className={styles.tableHorizontal}>Receipt No.</th>
                <th className={styles.tableHorizontal}>Date</th>
                <th className={styles.tableHorizontal}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {result?.contractFormTransactions?.map((each: any) => {
                totalDeposit += each.amountTransacted ?? 0;
                return (
                  <tr className={styles.tableRow}>
                    <td className={styles.tableDiagonal}>
                      {each.referenceNumber ?? ""}
                    </td>
                    <td className={styles.tableDiagonal}>
                      {each.dateOfTransaction ?? ""}
                    </td>
                    <td className={styles.tableDiagonal}>
                      {each.amountTransacted ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tr className={styles.headerTableRow}>
              <td className={styles.tableHorizontal}>Total Deposit</td>
              <td></td>
              <td className={styles.tableHorizontal}>{`$${totalDeposit}`}</td>
            </tr>
          </table>
        </div>
      </div>
    );
  };

  function calculateEndTime(startTime: any, eventDuration: any) {
    const [startHour, startMinute, meridiem] = startTime.split(/:| /);
    let endHour = parseInt(startHour);
    let endMeridiem = meridiem;
    endHour += eventDuration;
    if (endHour >= 12) {
      if (endHour > 12) {
        endHour -= 12;
      }
      endMeridiem = meridiem === "AM" ? "PM" : "AM";
    }
    const paddedHour = String(endHour).padStart(2, "0");
    const endTime = `${paddedHour}:${startMinute} ${endMeridiem}`;
    return endTime;
  }

  const actualChargesTableComponent = () => {
    let hall = null;
    return (
      <div>
        <h4>Actual Charges</h4>
        <div
          style={{ borderTopLeftRadius: "5px", borderTopRightRadius: "5px" }}
        >
          <table className={styles.table}>
            <thead>
              <tr className={styles.headerTableRow}>
                <th className={styles.tableHorizontal}>Event Hall</th>
                <th className={styles.tableHorizontal}>From</th>
                <th className={styles.tableHorizontal}>To</th>
                <th className={styles.tableHorizontal}>Total hrs</th>
                <th className={styles.tableHorizontal}>Price/hr</th>
                <th className={styles.tableHorizontal}>SetUp Cost</th>
                <th className={styles.tableHorizontal}>Total</th>
                {/* <th className={styles.tableHorizontal}>Discount</th>
                <th className={styles.tableHorizontal}>Reason for Discount</th> */}
              </tr>
            </thead>
            <tbody>
              {hallActualData?.map((each: any, ind: number) => {
                totalDeposit += each.amountTransacted ?? 0;
                hall = specificEventHall(each.eventHallId);
                totalHallsActual += Number(each.totalAmount);
                return (
                  <tr className={styles.tableRow} key={ind}>
                    <td className={styles.tableDiagonal}>
                      {each.eventHallName ?? ""}
                    </td>
                    <td className={styles.tableDiagonal}>
                      <TimeTextField
                        id={ind}
                        value={hallActualData?.[ind]?.eventStartTime}
                        onTimeChange={handleTimeChange}
                        onBlur={() => handleHoursDurationChange(ind)}
                      />
                    </td>
                    <td className={styles.tableDiagonal}>
                      {each.eventEndTime}
                    </td>
                    <td className={styles.tableDiagonal}>
                      {
                        <TextFieldForCount
                          id={ind}
                          onCountChange={handleHallActualHours}
                          value={hallActualData?.[ind]?.eventDuration}
                          onBlur={() => handleHoursDurationChange(ind)}
                        />
                      }
                    </td>
                    <td className={styles.tableDiagonal}>
                      {hall?.hourlyFee ?? ""}
                    </td>
                    <td className={styles.tableDiagonal}>
                      {hall?.setupAndCleaning ?? ""}
                    </td>
                    <td className={styles.tableDiagonal}>
                      {
                        <TextFieldForPrice
                          id={ind}
                          onValueChange={handleHallPriceChange}
                          value={hallActualData?.[ind]?.totalAmount}
                        />
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tr className={styles.headerTableRow}>
              <td className={styles.tableHorizontal} colSpan={6}>
                Total Deposit
              </td>
              <td
                className={styles.tableHorizontal}
              >{`$${totalHallsActual}`}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </table>
        </div>
      </div>
    );
  };

  const otherChargesTableComponent = () => {
    return (
      <div>
        <h4>Other Charges</h4>
        <div
          style={{ borderTopLeftRadius: "5px", borderTopRightRadius: "5px" }}
        >
          <table className={styles.table}>
            <thead>
              <tr className={styles.headerTableRow}>
                <th className={styles.tableHorizontal}>Description</th>
                <th className={styles.tableHorizontal}>Count/hrs</th>
                <th className={styles.tableHorizontal}>Price/Each</th>
                <th className={styles.tableHorizontal}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Priest Charges</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="priest"
                      onCountChange={handleCountChange}
                      value={values.priest.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="priest"
                      onValueChange={handlePriceChange}
                      value={values.priest.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>{values.priest.total}</td>
              </tr>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Round Tables</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="round"
                      onCountChange={handleCountChange}
                      value={values.round.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="round"
                      onValueChange={handlePriceChange}
                      value={values.round.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>{values.round.total}</td>
              </tr>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Rectangular Tables</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="rectangle"
                      onCountChange={handleCountChange}
                      value={values.rectangle.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="rectangle"
                      onValueChange={handlePriceChange}
                      value={values.rectangle.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {values.rectangle.total}
                </td>
              </tr>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Internet</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="internet"
                      onCountChange={handleCountChange}
                      value={values.internet.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="internet"
                      onValueChange={handlePriceChange}
                      value={values.internet.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {values.internet.total}
                </td>
              </tr>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Music System</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="music"
                      onCountChange={handleCountChange}
                      value={values.music.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="music"
                      onValueChange={handlePriceChange}
                      value={values.music.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>{values.music.total}</td>
              </tr>
              <tr className={styles.tableRow}>
                <td className={styles.tableDiagonal}>Mahaprasadam</td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForCount
                      id="mahaprasadam"
                      onCountChange={handleCountChange}
                      value={values.mahaprasadam.count}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {
                    <TextFieldForPrice
                      id="mahaprasadam"
                      onValueChange={handlePriceChange}
                      value={values.mahaprasadam.price}
                    />
                  }
                </td>
                <td className={styles.tableDiagonal}>
                  {values.mahaprasadam.total}
                </td>
              </tr>
            </tbody>
            <tr className={styles.headerTableRow}>
              <td className={styles.tableHorizontal}>Total Deposit</td>
              <td></td>
              <td></td>
              <td className={styles.tableHorizontal}>${getTotal()}</td>
            </tr>
            <tr className={styles.headerTableRow} style={{ marginTop: "10px" }}>
              <td className={styles.tableHorizontal}>
                {`Amount to be Paid to ${
                  getEstimatedTotal() > 0 ? "HCCC" : "Devotee"
                }`}
              </td>
              <td></td>
              <td></td>
              <td className={styles.tableHorizontal}>
                ${Math.abs(getEstimatedTotal())}
              </td>
            </tr>
          </table>
        </div>
      </div>
    );
  };

  const upperFields = () => {
    return (
      <HcccCard sx={{ p: 4 }}>
        <Grid container spacing={3}>
          <Grid xs={12} sm={3} item>
            {CustomRequiredInputLabel("Devotee Name", true)}
            <CustomTextFeild
              fullWidth
              sx={{
                backgroundColor: "#FFF9E4",
                borderRadius: "5px",
                border: "1px solid rgba(136, 46, 20, 0.4)",
              }}
              required
              value={result?.devoteeUsername}
              className={styles.textField}
            />
          </Grid>
          <Grid xs={12} sm={3} item>
            {CustomRequiredInputLabel("Event Date", false)}
            <CustomTextFeild
              fullWidth
              sx={{
                backgroundColor: "#FFF9E4",
                borderRadius: "5px",
                border: "1px solid rgba(136, 46, 20, 0.4)",
              }}
              required
              value={result?.eventDate}
              className={styles.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CustomCalendarTodayOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginTop: "10px" }}>
          <Grid xs={12} sm={6} item>
            {CustomRequiredInputLabel("Address", false)}
            <textarea
              style={{
                backgroundColor: "#FFF9E4",
                borderRadius: "5px",
                border: "1px solid rgba(136, 46, 20, 0.4)",
                padding: "8px",
                fontSize: "16px",
                width: "calc(100% - 14px)",
                minHeight: "79px",
                resize: "none",
                color: "rgba(0, 0, 0, 0.87)",
                maxHeight: "100px",
              }}
              required
              value={`${result?.city}, ${result?.state}, ${result?.country}, ${result?.zipcode}, ${result?.address} `}
              className={styles.textField}
            />
          </Grid>
        </Grid>
      </HcccCard>
    );
  };

  const footerFields = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <div>
          <CustomTextFeild
            fullWidth
            sx={{
              backgroundColor: "#FFF",
              borderRadius: "5px",
              border: "1px solid rgba(136, 46, 20, 0.4)",
            }}
            required
            value={formData.devoteeName}
            // className={styles.textField}
          />
          <CustomInputLabel sx={{ textAlign: "center" }}>
            Hccc Rep Sign & Date
          </CustomInputLabel>
        </div>
        <div>
          <CustomTextFeild
            fullWidth
            sx={{
              backgroundColor: "#FFF",
              borderRadius: "5px",
              border: "1px solid rgba(136, 46, 20, 0.4)",
            }}
            required
            value={formData.devoteeName}
            // className={styles.textField}
          />
          <CustomInputLabel sx={{ textAlign: "center" }}>
            User Sign & Date
          </CustomInputLabel>
        </div>
      </div>
    );
  };

  const footerButtons = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "20px",
        }}
      >
        <CommonOutlinedButton onClick={props?.handleSubmitReconciliation}>Cancel</CommonOutlinedButton>
        <CommonButton onClick={handleSubmit}>Submit</CommonButton>
      </div>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      {header()}
      {upperFields()}
      <div>
        {depositTableComponent()}
        {actualChargesTableComponent()}
        {otherChargesTableComponent()}
        {/* {footerFields()} */}
      </div>
      {footerButtons()}
    </div>
  );
};

export default FinalSettlementComponent;
